import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category.name} - 51吃瓜`,
          description: category.description,
          url: `https://51chigua.com/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '51吃瓜',
            url: 'https://51chigua.com',
          },
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 pb-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${category.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{category.name}</h1>
                  <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-400">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>
          </div>

          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-10">
            <div className="flex flex-col sm:flex-row gap-6">
              <aside className="w-full sm:w-48 flex-shrink-0">
                <h3 className="text-sm font-bold text-foreground-900 mb-3">全部分类</h3>
                <div className="flex flex-row sm:flex-col flex-wrap gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`whitespace-nowrap px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                {categoryArticles.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-foreground-500">暂无相关文章</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full font-medium">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-400 ml-2">{article.publishDate}</span>
                          <h3 className="text-sm md:text-base font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mt-2 leading-snug">
                            {article.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{article.excerpt}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}